// Filter command bar — Attio/Stripe-style. A row of segmented pills floating
// near the top of the map. Click a pill to open a popover with the actual
// options. Soft, rounded, generous padding, no all-caps tiny chips.
import { useEffect, useRef, useState } from 'react';
import { Check, ExternalLink, X } from 'lucide-react';
import { Link } from 'wouter';
import type { FilterState } from '../lib/events';
import {
  CATEGORY_COLOR,
  CATEGORY_LABEL,
  CATEGORY_ORDER,
} from '../lib/palette';
import type { Category, Secondary, EventStatus, Confidence } from '../lib/events';
import { filtersAreDefault, emptyFilters } from '../lib/url-state';
import { config } from '../lib/config';

interface Props {
  filters: FilterState;
  onChange: (next: FilterState) => void;
  visibleCount: number;
  totalCount: number;
  /** Inline mode renders the filter row without a glass-pill wrapper, for use
   *  inside the sidebar. The popover panels open below in-flow. */
  inline?: boolean;
}

type Segment = 'category' | 'secondary' | 'status' | 'confidence';

// Accept both legacy `string[]` and current `{id, label}[]` config shapes.
const SYNDROMES: Secondary[] = (
  (config.taxonomy.secondaryValues as
    | readonly (string | { id: string; label: string })[]
    | undefined) ?? []
).map((v) => (typeof v === 'string' ? v : v.id)) as Secondary[];
const STATUSES: EventStatus[] = ['active', 'concluded', 'historical'];
const CONFIDENCES: Confidence[] = ['verified', 'preliminary', 'unverified'];

export function FilterDrawer({ filters, onChange, visibleCount, totalCount, inline }: Props) {
  const [open, setOpen] = useState<Segment | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(null);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(null); };
    window.addEventListener('mousedown', onDocClick);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDocClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = <T extends string>(key: keyof FilterState, value: T) => {
    const cur = filters[key] as Set<string>;
    const next = new Set(cur);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    onChange({ ...filters, [key]: next });
  };

  const segCounts: Record<Segment, number> = {
    category: filters.categories.size,
    secondary: filters.secondaries.size,
    status: filters.statuses.size,
    confidence: filters.confidences.size,
  };

  const isDirty = !filtersAreDefault(filters);

  const wrapperClass = inline
    ? 'select-none'
    : 'pointer-events-auto select-none';
  const rowClass = inline
    ? 'flex flex-wrap items-center gap-1'
    : 'surface-glass flex h-10 items-center gap-1 px-1.5';
  const popClass = inline
    ? 'fade-in mt-2 w-full'
    : 'fade-in surface-elevated mt-2 w-[420px] p-3';

  return (
    <div ref={rootRef} className={wrapperClass}>
      <div className={rowClass}>
        {!inline && (
          <>
            <Counter visible={visibleCount} total={totalCount} />
            <span className="mx-0.5 h-4 w-px bg-[var(--color-line)]" />
          </>
        )}
        <Segment label="Category"   count={segCounts.category}   active={open === 'category'}   onClick={() => setOpen(open === 'category' ? null : 'category')} />
        <Segment label="Secondary"   count={segCounts.secondary}   active={open === 'secondary'}   onClick={() => setOpen(open === 'secondary' ? null : 'secondary')} />
        <Segment label="Status"     count={segCounts.status}     active={open === 'status'}     onClick={() => setOpen(open === 'status' ? null : 'status')} />
        <Segment label="Confidence" count={segCounts.confidence} active={open === 'confidence'} onClick={() => setOpen(open === 'confidence' ? null : 'confidence')} />
        {isDirty && (
          <>
            {!inline && <span className="mx-0.5 h-4 w-px bg-[var(--color-line)]" />}
            <button
              type="button"
              onClick={() => onChange(emptyFilters())}
              className="flex items-center gap-1 rounded-md px-2 py-1 text-[12px] font-medium text-[var(--color-text-3)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-signal)]"
            >
              <X size={12} strokeWidth={2} /> Clear
            </button>
          </>
        )}
      </div>

      {open && (
        <div className={popClass}>
          {open === 'category' && (
            <CategoryPanel
              selected={filters.categories}
              onToggle={(p) => toggle('categories', p)}
              onClear={() => onChange({ ...filters, categories: new Set() })}
            />
          )}
          {open === 'secondary' && (
            <PillRow
              title="Secondary"
              items={SYNDROMES}
              selected={filters.secondaries}
              onToggle={(s) => toggle('secondaries', s)}
              onClear={() => onChange({ ...filters, secondaries: new Set() })}
            />
          )}
          {open === 'status' && (
            <PillRow
              title="Status"
              items={STATUSES}
              selected={filters.statuses}
              onToggle={(s) => toggle('statuses', s)}
              onClear={() => onChange({ ...filters, statuses: new Set() })}
              dotForItem={(s) =>
                s === 'active' ? 'var(--color-signal)' : s === 'concluded' ? 'var(--color-text-3)' : 'var(--color-text-4)'
              }
            />
          )}
          {open === 'confidence' && (
            <PillRow
              title="Confidence"
              items={CONFIDENCES}
              selected={filters.confidences}
              onToggle={(c) => toggle('confidences', c)}
              onClear={() => onChange({ ...filters, confidences: new Set() })}
              dotForItem={(c) =>
                c === 'verified'
                  ? 'var(--color-verified)'
                  : c === 'preliminary'
                  ? 'var(--color-preliminary)'
                  : 'var(--color-unverified)'
              }
            />
          )}
        </div>
      )}
    </div>
  );
}

function Counter({ visible, total }: { visible: number; total: number }) {
  return (
    <div className="flex items-baseline gap-1.5 px-2.5 py-1 text-[13px]">
      <span className="font-semibold tabular-nums text-[var(--color-text)]">{visible}</span>
      <span className="text-[var(--color-text-4)]">/</span>
      <span className="tabular-nums text-[var(--color-text-3)]">{total}</span>
      <span className="ml-0.5 text-[12px] font-medium text-[var(--color-text-3)]">events</span>
    </div>
  );
}

function Segment({
  label, count, active, onClick,
}: { label: string; count: number; active: boolean; onClick: () => void }) {
  const hasFilter = count > 0;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={
        'inline-flex h-7 items-center gap-1.5 rounded-md border px-2.5 text-[12px] font-medium transition-all ' +
        (active
          ? 'border-[var(--color-accent)] bg-[var(--color-accent-soft)] text-[var(--color-accent)] shadow-[0_1px_2px_rgba(82,71,229,0.12)]'
          : hasFilter
          ? 'border-[var(--color-line-strong)] bg-white text-[var(--color-text)] shadow-sm hover:border-[var(--color-accent)] hover:text-[var(--color-accent)]'
          : 'border-[var(--color-line)] bg-[var(--color-surface-2)] text-[var(--color-text-2)] hover:border-[var(--color-line-strong)] hover:bg-white hover:text-[var(--color-text)]')
      }
    >
      <span>{label}</span>
      {hasFilter && (
        <span
          className={
            'rounded-full px-1.5 py-px text-[10.5px] font-semibold tabular-nums ' +
            (active ? 'bg-[var(--color-accent)] text-white' : 'bg-[var(--color-accent-soft)] text-[var(--color-accent)]')
          }
        >
          {count}
        </span>
      )}
    </button>
  );
}

function CategoryPanel({
  selected, onToggle, onClear,
}: {
  selected: Set<string>;
  onToggle: (p: Category) => void;
  onClear: () => void;
}) {
  return (
    <div>
      <PanelHeader title="Category" hasSelection={selected.size > 0} onClear={onClear} />
      <div className="grid grid-cols-2 gap-1">
        {CATEGORY_ORDER.map(p => (
          <CategoryRow
            key={p}
            category={p}
            active={selected.has(p)}
            onClick={() => onToggle(p)}
          />
        ))}
      </div>
    </div>
  );
}

function CategoryRow({
  category, active, onClick,
}: { category: Category; active: boolean; onClick: () => void }) {
  const info = CATEGORY_LABEL[category];
  const color = CATEGORY_COLOR[category];
  const hasDetailPage = true;
  return (
    <div
      className={
        'group flex h-8 items-center gap-2 rounded-md pr-1 text-left text-[13px] transition-colors ' +
        (active ? 'bg-[var(--color-accent-soft)]' : 'hover:bg-[var(--color-surface-2)]')
      }
    >
      <button
        type="button"
        onClick={onClick}
        title={info.name}
        className="flex flex-1 items-center gap-2 truncate rounded-md px-2 py-0.5"
      >
        <span
          className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
          style={{
            background: color,
            boxShadow: '0 0 0 2px white, 0 0 0 3px rgba(0,0,0,0.06)',
          }}
        />
        <span className="flex flex-1 items-baseline gap-1.5 truncate">
          <span className="mono text-[11px] font-semibold text-[var(--color-text)]">
            {info.abbreviation}
          </span>
          <span className="truncate text-[12px] text-[var(--color-text-3)]">
            {info.name}
          </span>
        </span>
        {active && (
          <Check size={13} strokeWidth={2.5} className="text-[var(--color-accent)]" />
        )}
      </button>
      {hasDetailPage && (
        <Link
          href={`/category/${category}`}
          title={`Open ${info.abbreviation} reference page`}
          className="inline-flex h-6 w-6 items-center justify-center rounded text-[var(--color-text-4)] opacity-0 transition-opacity hover:bg-white hover:text-[var(--color-accent)] group-hover:opacity-100"
          onClick={(e) => e.stopPropagation()}
        >
          <ExternalLink size={11} strokeWidth={2} />
        </Link>
      )}
    </div>
  );
}

function PillRow<T extends string>({
  title, items, selected, onToggle, onClear, dotForItem,
}: {
  title: string;
  items: readonly T[];
  selected: Set<string>;
  onToggle: (v: T) => void;
  onClear: () => void;
  dotForItem?: (v: T) => string;
}) {
  return (
    <div>
      <PanelHeader title={title} hasSelection={selected.size > 0} onClear={onClear} />
      <div className="flex flex-wrap gap-1.5">
        {items.map(v => {
          const active = selected.has(v);
          const dot = dotForItem?.(v);
          return (
            <button
              key={v}
              type="button"
              onClick={() => onToggle(v)}
              className={
                'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12.5px] font-medium transition-colors ' +
                (active
                  ? 'bg-[var(--color-accent)] text-white'
                  : 'bg-[var(--color-surface-2)] text-[var(--color-text-2)] hover:bg-[var(--color-surface-3)] hover:text-[var(--color-text)]')
              }
            >
              {dot && (
                <span
                  className="inline-block h-1.5 w-1.5 rounded-full"
                  style={{ background: active ? 'rgba(255,255,255,0.85)' : dot }}
                />
              )}
              <span className="capitalize">{v}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function PanelHeader({
  title, hasSelection, onClear,
}: { title: string; hasSelection: boolean; onClear: () => void }) {
  return (
    <div className="mb-2 flex items-center justify-between">
      <span className="text-[12px] font-medium uppercase tracking-wide text-[var(--color-text-3)]">
        {title}
      </span>
      {hasSelection && (
        <button
          type="button"
          onClick={onClear}
          className="text-[12px] font-medium text-[var(--color-text-3)] hover:text-[var(--color-accent)]"
        >
          Clear
        </button>
      )}
    </div>
  );
}
